import { action, observable } from 'mobx'
import { validationContext } from 'validx'
import { Id } from '@/infra/id'
import { InventoryItem } from '@/features/inventory/InventoryItem'
import { type JobScopeItem } from '@/features/jobs/Job'

/**
 * An inventory service item in the job scope being edited.
 */
export class JobScopeInventoryItemViewModel {
  /**
   * The inventory item.
   */
  @observable accessor id: Id = Id.none

  /**
   * The inventory item version.
   */
  @observable accessor versionId: Id = Id.none

  /**
   * The item name.
   */
  @observable accessor name = ''

  /**
   * The item description.
   */
  @observable accessor description = ''

  /**
   * Whether the item is picked.
   */
  @observable accessor selected = false

  /**
   * The quantity the updated scope holds.
   */
  @observable accessor newQuantity = 1

  /**
   * The quantity the job scope holds.
   */
  @observable accessor currentQuantity = 0

  /**
   * The client price.
   */
  @observable accessor price = 0

  /**
   * The Pro price.
   */
  @observable accessor proPrice = 0

  /**
   * The bundle the item came with, if any.
   */
  @observable accessor bundleId: Id | null

  /**
   * Whether the item is listed; removed job items are hidden.
   */
  @observable accessor canShow = true

  /**
   * Validation context for the quantity.
   */
  readonly validation = validationContext(this, {
    newQuantity: [
      (opts) =>
        opts.value >= 1 || opts.value <= -1 || 'Quantity cannot be zero.',
    ],
  })

  constructor(
    item: InventoryItem | JobScopeItem,
    private readonly _onSelectedChange: (
      item: JobScopeInventoryItemViewModel,
    ) => void,
  ) {
    this.id = item instanceof InventoryItem ? item.id : item.inventoryItemId
    this.versionId =
      item instanceof InventoryItem
        ? item.versionId
        : item.inventoryItemVersionId
    this.name = item.name
    this.description = item.description
    this.currentQuantity = item instanceof InventoryItem ? 0 : item.quantity
    this.newQuantity = item instanceof InventoryItem ? 1 : item.quantity
    this.selected = true
    this.price = item.price ?? 0
    this.proPrice = item.proPrice ?? 0
    this.bundleId = item instanceof InventoryItem ? null : item.bundleId
  }

  /**
   * Validates the item and returns whether it is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Sets the quantity after the update; clearing the field means one.
   */
  @action.bound
  setQuantity(value: number | null) {
    this.newQuantity = value ?? 1
  }

  /**
   * Toggles whether the item is selected.
   */
  @action.bound
  toggle() {
    this.selected = !this.selected
    this._onSelectedChange(this)
  }

  /**
   * Hides the item from the list, and zeroes its quantity so submitting
   * removes it from the job scope.
   */
  @action.bound
  removeFromJobScope() {
    this.canShow = false
    this.newQuantity = 0
  }
}
